// UserContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { getSasToken } from '../../services/apiService';

export const UserContext = createContext();

// Refresh token 1 hour before expiry
const TOKEN_REFRESH_BUFFER_MS = 60 * 60 * 1000;

export const UserProvider = ({ children }) => {
    const { accounts, instance } = useMsal();
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState('');
    const [role, setRole] = useState('');
    const [sasToken, setSasToken] = useState('');
    const [sasTokenExpiry, setSasTokenExpiry] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    // Function to refresh the SAS token
    const refreshSasToken = useCallback(async () => {
        try {
            const tokenResult = await getSasToken();
            setSasToken(tokenResult.sasToken);
            setSasTokenExpiry(tokenResult.expiresOn);
            localStorage.setItem('sasToken', tokenResult.sasToken);
            localStorage.setItem('sasTokenExpiry', tokenResult.expiresOn);
            console.log('SAS token refreshed, expires:', tokenResult.expiresOn);
            return tokenResult;
        } catch (error) {
            console.error('Failed to refresh SAS token:', error);
            throw error;
        }
    }, []);

    // Function to check if token needs refresh
    const isTokenExpiringSoon = useCallback(() => {
        if (!sasTokenExpiry) return true;
        const expiryDate = new Date(sasTokenExpiry);
        const now = new Date();
        const timeUntilExpiry = expiryDate.getTime() - now.getTime();
        return timeUntilExpiry <= TOKEN_REFRESH_BUFFER_MS;
    }, [sasTokenExpiry]);

    // Function to ensure valid token - call this before operations that need the token
    const ensureValidSasToken = useCallback(async () => {
        if (isTokenExpiringSoon()) {
            await refreshSasToken();
        }
        return localStorage.getItem('sasToken');
    }, [isTokenExpiringSoon, refreshSasToken]);

    useEffect(() => {
        // Load user data and SAS token info from localStorage
        const storedUserName = localStorage.getItem('userName');
        const storedUserId = localStorage.getItem('userId');
        const storedRole = localStorage.getItem('role');
        const storedSasToken = localStorage.getItem('sasToken');
        const storedSasTokenExpiry = localStorage.getItem('sasTokenExpiry');

        if (storedUserName && storedUserId && storedRole && storedSasToken && storedSasTokenExpiry) {
            setUserName(storedUserName);
            setUserId(storedUserId);
            setRole(storedRole);
            setSasToken(storedSasToken);
            setSasTokenExpiry(storedSasTokenExpiry);
            setIsAuthenticated(true);
        } else if (accounts.length > 0) {
            // Fallback if localStorage is empty but there is a valid MSAL account
            const account = accounts[0];
            const defaultRole = account.idTokenClaims?.roles?.[0] || 'Role.User';

            setUserName(account.name);
            setUserId(account.username);
            setRole(defaultRole);
            setIsAuthenticated(true);

            localStorage.setItem('userName', account.name);
            localStorage.setItem('userId', account.username);
            localStorage.setItem('role', defaultRole);

            // Fetch a new SAS token
            (async () => {
                try {
                    const tokenResult = await getSasToken();
                    setSasToken(tokenResult.sasToken);
                    setSasTokenExpiry(tokenResult.expiresOn);
                    localStorage.setItem('sasToken', tokenResult.sasToken);
                    localStorage.setItem('sasTokenExpiry', tokenResult.expiresOn);
                } catch (error) {
                    console.error('Failed to fetch initial SAS token:', error);
                }
            })();
        } else {
            setIsAuthenticated(false);
        }

        setLoading(false);
    }, [accounts]);

    // Check for SAS token expiration and proactively refresh
    useEffect(() => {
        if (!sasTokenExpiry || !isAuthenticated) return;

        const expiryDate = new Date(sasTokenExpiry);
        const now = new Date();
        const timeUntilExpiry = expiryDate.getTime() - now.getTime();

        // If token has already expired, sign out
        if (timeUntilExpiry <= 0) {
            localStorage.clear();
            setIsAuthenticated(false);
            instance.logoutRedirect({
                postLogoutRedirectUri: 'https://oil.spm-automation.com/signin',
            });
            return;
        }

        // Schedule a refresh 1 hour before expiry
        const refreshTime = Math.max(timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS, 0);
        
        const refreshTimer = setTimeout(async () => {
            try {
                await refreshSasToken();
            } catch (error) {
                console.error('Scheduled token refresh failed:', error);
            }
        }, refreshTime);

        return () => clearTimeout(refreshTimer);
    }, [sasTokenExpiry, isAuthenticated, instance, refreshSasToken]);

    return (
        <UserContext.Provider
            value={{
                userName,
                setUserName,
                userId,
                setUserId,
                role,
                setRole,
                sasToken,
                setSasToken,
                sasTokenExpiry,
                setSasTokenExpiry,
                isAuthenticated,
                setIsAuthenticated,
                loading,
                refreshSasToken,
                ensureValidSasToken,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
